import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDispatch } from 'shared/config/redux/hooks/useRedux'

import { changeNotificationsParams } from '../../model/slice'
import { Notification } from '../../types'

interface Params {
  notifications: Notification[] | undefined
  limit: number | undefined
  intersectionObserverIndex: number
  count: number | null
  lastPage: number | null
  isFetching: boolean
}

export const useNotificationsObserve = ({
  notifications,
  limit,
  intersectionObserverIndex,
  count,
  lastPage,
  isFetching,
}: Params) => {
  const dispatch = useAppDispatch()

  const currentPageRef = useRef<number>()
  const notificationsLengthRef = useRef(notifications?.length ?? 0)
  const lastObservedOrderRef = useRef<number | null>()

  const observer = useRef<IntersectionObserver | null>(null)

  const [nextDataCount, setNextDataCount] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)

  const updateNextDataCount = () => {
    if (
      typeof count === 'number' &&
      typeof lastPage === 'number' &&
      typeof limit === 'number'
    ) {
      setNextDataCount(Math.min(count - lastPage * limit, limit))
    } else if (typeof limit === 'number') {
      setNextDataCount(limit)
    } else setNextDataCount(null)
  }

  const handleUpdateData = (
    currentIndex: number,
    nextPage: number,
    limit: number,
  ) => {
    const lastPossibleElement =
      limit *
        Math.floor(
          (notificationsLengthRef.current - intersectionObserverIndex) / limit,
        ) +
      intersectionObserverIndex
    const isLast = lastPossibleElement === currentIndex + 1
    if (isLast) {
      lastObservedOrderRef.current = currentIndex
      updateNextDataCount()
      dispatch(
        changeNotificationsParams({
          page: nextPage,
          limit,
          isUpdate: false,
        }),
      )
      setCurrentPage(nextPage)
    }
  }

  const createObserver = useCallback(
    (index: number) => (node: HTMLDivElement | null) => {
      if (isFetching || !node || !limit) return
      const prevPage = Math.max(1, Math.ceil((index + 1) / limit))

      if (!observer.current) {
        observer.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const currentIndex = Number(
                entry.target.getAttribute('data-index'),
              )

              const nextPage = Math.ceil(currentIndex / limit) + 1
              if (entry.isIntersecting) {
                if ((currentIndex + 1) % limit === 1) {
                  const observedPage = Number(
                    entry.target.getAttribute('data-prev-page'),
                  )
                  setCurrentPage(observedPage)
                }
                if (
                  (currentIndex + 1) % limit === intersectionObserverIndex &&
                  (!lastPage || nextPage !== currentPageRef.current) &&
                  notificationsLengthRef.current &&
                  lastObservedOrderRef.current !== currentIndex
                ) {
                  console.log(nextPage, 'yeahhh')
                  handleUpdateData(currentIndex, nextPage, limit)
                }
              }
            })
          },
          {
            threshold: [0, 0.5, 1.0],
          },
        )
      }
      if ((index + 1) % limit === 1) node.dataset.prevPage = prevPage.toString()
      observer.current.observe(node)
    },
    [isFetching, limit, lastPage, intersectionObserverIndex],
  )

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    if (
      lastObservedOrderRef.current &&
      notifications &&
      notifications.length < lastObservedOrderRef.current
    ) {
      lastObservedOrderRef.current = null
    }
    notificationsLengthRef.current = notifications?.length ?? 0
  }, [notifications])

  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  useEffect(() => {
    if (limit) setNextDataCount(limit)
  }, [limit])

  return {
    createObserver,
    nextDataCount,
    setNextDataCount,
    currentPage,
  }
}
